import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useEffect, useRef, useState } from "react";
import Loading from "../../../../../../components/Loading/Loading";
import EmailSecurityCodeField from "../../../../../../components/EmailSecurityCodeField/EmailSecurityCodeField";
import localeText from "../../../../../../locale";
import ExtracoinService from "../../../../../../service/ExtracoinService";
import handleLocale from "../../../../../../utilities/LocaleSwitch";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import { locale } from "../../../../../../utilities/locale/locale";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import UserInitialsAvatar from "../../../../../../components/UserInitialsAvatar/UserInitialsAvatar";

function getTimeAgo(dateStamp) {
  const diff = Date.now() - new Date(dateStamp).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins} min`;
  if (hours < 24) return `${hours} h`;
  return `${days} dias`;
}

const PendingExtracreditReceipts = ({ currencySymbol, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [loadingAccept, setLoadingAccept] = useState(false);
  const [intentsReceived, setIntentsReceived] = useState([]);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [code, setCode] = useState("");
  const [securityCode, setSecurityCode] = useState("");
  const codeRef = useRef(null);
  const securityCodeRef = useRef(null);

  const extracoinService = new ExtracoinService();
  const localeISO = handleLocale(locale);
  const { showErrorToast, showSuccessToast } = useToastContext();
  const fetchIntents = async () => {
    setLoading(true);
    try {
      const res = await extracoinService.getIntentsTo(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
      );
      if (res.messageCode === 200001) {
        setIntentsReceived(
          res.result.filter((intent) => intent.agreementFrom && !intent.agreementTo),
        );
      } else {
        setIntentsReceived([]);
      }
    } catch (e) {
      if (e?.status === 401 || e?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      setIntentsReceived([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntents();
  }, []);

  const handleApproveClick = (intent) => {
    setSelectedIntent(intent);
    setCode("");
    setSecurityCode("");
    setConfirmVisible(true);
  };

  const handleRejectClick = () => {
    showErrorToast({ detail: localeText("rejectNotAvailable") || "Reject not available." });
  };

  const acceptTransaction = async () => {
    if (!selectedIntent) return;
    if (!code?.trim()) {
      showErrorToast({ detail: localeText("codeCantBeEmpty") });
      if (codeRef.current) codeRef.current.style.border = "1px solid red";
      return;
    }
    if (!securityCode?.trim()) {
      showErrorToast({ detail: localeText("securityCodeCantBeEmpty") });
      if (securityCodeRef.current) securityCodeRef.current.style.border = "1px solid red";
      return;
    }
    if (codeRef.current) codeRef.current.style.border = "1px solid #ced4da";
    if (securityCodeRef.current) securityCodeRef.current.style.border = "1px solid #ced4da";

    setLoadingAccept(true);
    try {
      const res = await extracoinService.postToAgree(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        selectedIntent.id,
        code,
        securityCode.trim(),
      );
      if (res.messageCode === 500166) {
        showErrorToast({ detail: localeText("securityCodeIsWrong") });
      } else if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
        showErrorToast({ detail: msg || localeText("error") });
      } else {
        showSuccessToast({ detail: localeText("transactionReceived") });
        setConfirmVisible(false);
        setSelectedIntent(null);
        await fetchIntents();
        onRefresh?.();
      }
    } catch (e) {
      if (e?.status === 401 || e?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      showErrorToast({ detail: localeText("error") });
    } finally {
      setLoadingAccept(false);
    }
  };

  const getSenderDisplay = (intent) => {
    const u = intent.userFrom;
    if (!u) return localeText("sender") || "Sender";
    return u.name || u.firstName || u.username || localeText("sender");
  };

  if (loading || intentsReceived.length === 0) return null;

  return (
    <>
      <div className="mb-4">
        <div className="flex flex-column gap-3">
          {intentsReceived.map((intent) => (
            <div
              key={intent.id}
              className="flex flex-wrap gap-3 p-3 border-round-md"
              style={{ backgroundColor: "#fff", border: "1px solid #eee" }}
            >
              <div
                className="flex align-items-center justify-content-center border-round-circle overflow-hidden"
                style={{
                  width: "48px",
                  height: "48px",

                  flexShrink: 0,
                }}
              >
                <UserInitialsAvatar
                  avatarUrl={intent?.userFrom?.urlAvatar}
                  firstName={intent?.userFrom?.firstName || intent?.userFrom?.firstname}
                  lastName={intent?.userFrom?.lastName || intent?.userFrom?.surname}
                  fullName={intent?.userFrom?.name || intent?.userFrom?.username}
                  style={{ width: "100%", height: "100%", fontSize: "1rem" }}
                  alt="sender-avatar"
                />
              </div>
              <div>
                <div className="flex align-items-center gap-3">
                  <div>
                    <h4 className="font-medium m-0 mb-1 text-sm" style={{ color: "#171717" }}>
                      {localeText("pendingReceipts")}
                    </h4>
                    <p className="font-medium m-0 mb-1 text-xs" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                      {getTimeAgo(intent.date || intent.createdAt || Date.now())} ·{" "}
                      {getSenderDisplay(intent)} {localeText("pendingReceiptsSent") || "enviou"}{" "}
                      {intent.amountTo?.toLocaleString(localeISO, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      ECR.{" "}
                      {localeText("pendingReceiptsHint") ||
                        "These values will only be credited after acceptance."}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  {/* <Button
                    label={localeText("reject") || "Recusar"}
                    className="p-button-text p-button-outlined"
                    onClick={handleRejectClick}
                  /> */}
                  <Button
                    label={localeText("approve") || "Aprovar"}
                    onClick={() => handleApproveClick(intent)}
                    className="border-round-md p-no-outline"
                    style={{
                      backgroundColor: "var(--theme-primary, #1DAF61)",
                      color: "#fff",
                      border: "none",
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog
        header={
          <>
            <i className="pi pi-exclamation-triangle text-2xl pr-2" />
            <span className="font-bold m-0 p-0 text-2xl">
              {localeText("attention").toUpperCase()}:
            </span>{" "}
            {localeText("confirmYourAction")}
          </>
        }
        visible={confirmVisible}
        className="w-30rem mx-2"
        onHide={() => !loadingAccept && setConfirmVisible(false)}
        draggable={false}
      >
        <div className="mr-3 ml-3">
          {selectedIntent?.taxTo != null && (
            <p>
              {localeText("confirmTransactionExplanation")}{" "}
              <b className="text-xl">
                {currencySymbol} {formatPrice(selectedIntent.taxTo)}.
              </b>
            </p>
          )}
          <p>{localeText("toAgreeAndReceiveInsertCode")}:</p>
          <InputText
            ref={codeRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onFocus={() => codeRef.current && (codeRef.current.style.border = "1px solid #ced4da")}
            className="w-full mb-3"
          />
          <EmailSecurityCodeField
            ref={securityCodeRef}
            value={securityCode}
            onChange={setSecurityCode}
            onFocus={() =>
              securityCodeRef.current && (securityCodeRef.current.style.border = "1px solid #ced4da")
            }
            disabled={loadingAccept}
          />
          <p className="mt-2">{localeText("doYouAgree")}</p>
          <div className="flex gap-2 justify-content-end">
            <Button
              label={localeText("no")}
              severity="danger"
              onClick={() => setConfirmVisible(false)}
              disabled={loadingAccept}
            />
            <Button
              onClick={acceptTransaction}
              disabled={loadingAccept}
              className={loadingAccept ? "opacity-80" : ""}
            >
              {loadingAccept ? (
                <span className="flex align-items-center gap-2">
                  <Loading classNameIcon="text-sm" disableMargin />
                  {localeText("yes")}
                </span>
              ) : (
                localeText("yes")
              )}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default PendingExtracreditReceipts;
